(() => {
  function initTabs(page) {
    const tabGroups = Array.from(page.querySelectorAll('[data-supplies-tabs]'));

    tabGroups.forEach((tabGroup) => {
      if (tabGroup.dataset.suppliesTabsInitialized === 'true') return;

      const tabs = Array.from(tabGroup.querySelectorAll('[data-supplies-tab]'));
      const panels = Array.from(tabGroup.querySelectorAll('[data-supplies-tab-panel]'));
      if (!tabs.length || !panels.length) return;

      tabGroup.dataset.suppliesTabsInitialized = 'true';

      const activateTab = (tabName, options = {}) => {
        const activeTab = tabs.find((tab) => tab.dataset.suppliesTab === tabName) || tabs[0];
        const activeName = activeTab.dataset.suppliesTab;

        tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.suppliesTabPanel === activeName;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        if (options.focus) {
          activeTab.focus();
        }

        if (options.updateHash && window.history && typeof window.history.replaceState === 'function') {
          const activePanel = panels.find((panel) => panel.dataset.suppliesTabPanel === activeName);
          if (activePanel?.id) {
            window.history.replaceState(null, '', `#${activePanel.id}`);
          }
        }

        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          activateTab(tab.dataset.suppliesTab, { updateHash: true });
        });

        tab.addEventListener('keydown', (event) => {
          let nextIndex = index;

          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            nextIndex = (index + 1) % tabs.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            nextIndex = (index - 1 + tabs.length) % tabs.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = tabs.length - 1;
          } else {
            return;
          }

          event.preventDefault();
          activateTab(tabs[nextIndex].dataset.suppliesTab, { focus: true, updateHash: true });
        });
      });

      const hash = window.location.hash.replace('#', '');
      const hashPanel = panels.find((panel) => panel.id === hash);
      const initialTabName = hashPanel?.dataset.suppliesTabPanel || tabs[0].dataset.suppliesTab;

      activateTab(initialTabName);
    });
  }

  function initCarousels(page) {
    const carousels = Array.from(page.querySelectorAll('[data-supplies-carousel]'));

    carousels.forEach((carousel) => {
      if (carousel.dataset.suppliesCarouselInitialized === 'true') return;
      carousel.dataset.suppliesCarouselInitialized = 'true';

      const viewport = carousel.querySelector('[data-supplies-carousel-viewport]');
      const prevButton = carousel.querySelector('[data-carousel-prev]');
      const nextButton = carousel.querySelector('[data-carousel-next]');

      if (!viewport) return;

      const updateButtons = () => {
        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const hasOverflow = maxScroll > 8;

        if (prevButton) {
          prevButton.disabled = !hasOverflow || viewport.scrollLeft <= 4;
        }

        if (nextButton) {
          nextButton.disabled = !hasOverflow || viewport.scrollLeft >= maxScroll - 4;
        }
      };

      const scrollAmount = () => Math.max(260, Math.round(viewport.clientWidth * 0.82));

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          viewport.scrollBy({ left: scrollAmount() * -1, behavior: 'smooth' });
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          viewport.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        });
      }

      viewport.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);
      carousel.addEventListener('supplies:carousel:refresh', () => {
        requestAnimationFrame(updateButtons);
        setTimeout(updateButtons, 200);
      });
      requestAnimationFrame(updateButtons);
      setTimeout(updateButtons, 200);
    });
  }

  function initNewestReleaseFilters(page) {
    const browsers = Array.from(page.querySelectorAll('[data-newest-releases]'));

    browsers.forEach((browser) => {
      if (browser.dataset.newestFiltersInitialized === 'true') return;

      const buttons = Array.from(browser.querySelectorAll('[data-newest-filter]'));
      const products = Array.from(browser.querySelectorAll('[data-newest-product]'));
      const carouselContainer = browser.querySelector('[data-newest-carousel]');
      const carousel = browser.querySelector('[data-supplies-carousel]');
      const viewport = browser.querySelector('[data-supplies-carousel-viewport]');
      const emptyState = browser.querySelector('[data-newest-empty]');
      const status = browser.querySelector('[data-newest-status]');

      if (!buttons.length || !products.length || !carouselContainer) return;
      browser.dataset.newestFiltersInitialized = 'true';

      const applyFilter = (filterName, options = {}) => {
        const activeButton = buttons.find((button) => button.dataset.newestFilter === filterName) || buttons[0];
        const activeFilter = activeButton.dataset.newestFilter || 'all';
        const activeLabel = activeButton.textContent.trim();
        let visibleCount = 0;

        buttons.forEach((button) => {
          const isActive = button === activeButton;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        products.forEach((product) => {
          const matchesFormat = product.dataset.newestFormat === activeFilter;
          const matchesBrand = product.dataset.newestBrand === activeFilter;
          const isVisible = activeFilter === 'all' || matchesFormat || matchesBrand;

          product.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        carouselContainer.hidden = visibleCount === 0;

        if (emptyState) {
          emptyState.hidden = visibleCount !== 0;
          emptyState.textContent = visibleCount === 0
            ? `No ${activeLabel} products are currently in Newest Releases.`
            : '';
        }

        if (status) {
          status.textContent = options.announce && visibleCount > 0
            ? `${visibleCount} ${visibleCount === 1 ? 'product' : 'products'} shown for ${activeLabel}.`
            : '';
        }

        if (viewport) {
          viewport.scrollLeft = 0;
        }

        if (carousel) {
          carousel.dispatchEvent(new CustomEvent('supplies:carousel:refresh'));
        }
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => applyFilter(button.dataset.newestFilter, { announce: true }));
      });

      applyFilter('all');
    });
  }

  function initVariantCards(page) {
    const cards = Array.from(page.querySelectorAll('[data-supplies-variant-card]'));

    cards.forEach((card) => {
      if (card.dataset.suppliesVariantInitialized === 'true') return;
      card.dataset.suppliesVariantInitialized = 'true';

      const swatches = Array.from(card.querySelectorAll('[data-supplies-swatch]'));
      if (!swatches.length) return;

      const image = card.querySelector('[data-supplies-image]');
      const colourLabel = card.querySelector('[data-supplies-selected-colour]');
      const priceCurrent = card.querySelector('[data-supplies-price-current]');
      const priceCompare = card.querySelector('[data-supplies-price-compare]');
      const variantInput = card.querySelector('[data-supplies-variant-id-input]');
      const addToCartButton = card.querySelector('[data-supplies-add-to-cart]');
      const buttonText = card.querySelector('[data-supplies-button-text]');
      const productLinks = Array.from(card.querySelectorAll('[data-supplies-product-link]'));
      const addLabel = addToCartButton?.dataset.addLabel || 'Add to cart';
      const soldOutLabel = addToCartButton?.dataset.soldOutLabel || 'Sold out';

      const applyVariant = (swatch) => {
        if (!swatch) return;

        swatches.forEach((item) => {
          const isActive = item === swatch;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        if (variantInput) {
          variantInput.value = swatch.dataset.variantId || '';
        }

        if (colourLabel) {
          colourLabel.textContent = `Colour - ${swatch.dataset.colour || ''}`;
        }

        if (priceCurrent) {
          priceCurrent.textContent = swatch.dataset.price || priceCurrent.textContent;
        }

        if (priceCompare) {
          const compareAt = swatch.dataset.compareAt || '';
          priceCompare.textContent = compareAt;
          priceCompare.hidden = !compareAt;
        }

        if (image) {
          const imageUrl = swatch.dataset.imageUrl || '';
          const imageSrcset = swatch.dataset.imageSrcset || '';

          if (imageUrl) {
            image.src = imageUrl;
          }

          if (imageSrcset) {
            image.srcset = imageSrcset;
          } else {
            image.removeAttribute('srcset');
          }

          if (swatch.dataset.imageAlt) {
            image.alt = swatch.dataset.imageAlt;
          }
        }

        if (productLinks.length && swatch.dataset.variantUrl) {
          productLinks.forEach((link) => {
            link.setAttribute('href', swatch.dataset.variantUrl);
          });
        }

        const isAvailable = swatch.dataset.available === 'true';
        if (addToCartButton) {
          addToCartButton.disabled = !isAvailable;
          if (isAvailable) {
            addToCartButton.removeAttribute('aria-disabled');
          } else {
            addToCartButton.setAttribute('aria-disabled', 'true');
          }
        }

        if (buttonText) {
          buttonText.textContent = isAvailable ? addLabel : soldOutLabel;
        }
      };

      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => applyVariant(swatch));
      });

      const initialSwatch = swatches.find((swatch) => swatch.classList.contains('is-active')) || swatches[0];
      applyVariant(initialSwatch);
    });
  }

  function initPage(page) {
    if (!page || page.dataset.suppliesPageInitialized === 'true') return;
    page.dataset.suppliesPageInitialized = 'true';

    initTabs(page);
    initCarousels(page);
    initNewestReleaseFilters(page);
    initVariantCards(page);
  }

  function initAll(root = document) {
    const pages = Array.from(root.querySelectorAll('.ls-supplies-page'));
    pages.forEach((page) => initPage(page));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
